import Typography from "@mui/material/Typography";
import {
  Grid,
  IconButton,
  TextField,
  LinearProgress,
  Card,
  CardContent,
  Chip,
  Tooltip,
  Stack,
  TablePagination,
  useMediaQuery,
} from "@mui/material";
import Box from "@mui/material/Box";
import { useTheme } from "@emotion/react";
import { DataGrid } from "@mui/x-data-grid";
import { BiTrash, BiEdit } from "react-icons/bi";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Product,
  setSearchBody,
} from "../../../../../app/slices/productsSlice";
import ConfirmationDialog from "../../../../../helpers/ConfirmationDialog";
import { MdOutlineClear } from "react-icons/md";
import { SelectSubCategory } from "../../../../../components/Selectors/SelectSubCategory";
import Permission from "../../../../../components/userPermission/userPermission";

const ProductsTable = () => {
  const dispatch = useDispatch();

  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const _Role = useSelector((state) => state.user.userInfo.user_roles[0]);
  const _Product = useSelector((state) => state.product.products);
  const loading = useSelector((state) => state.product.loading);
  const _SearchBody = useSelector((state) => state.product.searchBody);
  const _SubCategory = useSelector(
    (state) => state.subCategories.subCategoriesList.data
  );
  const _UserInfo = useSelector((state) => state.user.userInfo);
  const PRIVILEGED_USER_ID = "ca40271f-cd02-4f18-b48a-83ae2e2b6fef";

  const showRevenue =
    _Role === "super_admin" ||
    (_UserInfo && _UserInfo.id === PRIVILEGED_USER_ID);

  const [search, setSearch] = useState({
    search: "",
    sub_category_id: null,
  });
  const [page, setPage] = useState(15);
  const [currentPage, setCurrentPage] = useState(0);
  const [openConfirm, setConfirmOpen] = useState(false);
  const [dialogData, setDialogData] = useState("");
  const [openActive, setOpenActive] = useState(false);
  const [dialogActiveData, setDialogActiveData] = useState({});

  const handleDeleteClick = (params) => {
    setConfirmOpen(true);
    setDialogData(params);
  };

  const handleConfirmDelete = () => {
    dispatch(Product.deleteById(dialogData.id, _SearchBody));
    setConfirmOpen(false);
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
  };

  const handleActiveClick = (params) => {
    setOpenActive(true);
    setDialogActiveData(params);
  };

  const handleConfirmActive = () => {
    dispatch(
      Product.activeProduct(
        dialogActiveData.id,
        {
          is_active: !dialogActiveData.is_active,
        },
        _SearchBody
      )
    );
    setOpenActive(false);
  };

  const handleCancelActive = () => {
    setOpenActive(false);
  };

  const columns = [
    {
      field: "index",
      headerName: "#",
      flex: 0.3,
    },
    {
      field: "sequence",
      headerName: "التسلسل",
      flex: 0.5,
    },
    {
      field: "name",
      headerName: "الاسم",
      flex: 1.5,
      renderCell: (params) => {
        return (
          <Typography
            sx={{
              WebkitLineBreak: "after-white-spaces",
              whiteSpace: "break-spaces",
            }}
          >
            {params.row?.name}
          </Typography>
        );
      },
    },
    {
      field: "sub_category_name",
      headerName: "التصنيف",
      flex: 1,
    },
    {
      field: "selling_price",
      headerName: "سعر البيع",
      flex: 1,
      renderCell: (params) => {
        return <>{parseInt(params.row?.selling_price)?.toLocaleString()}</>;
      },
    },
    ...(showRevenue
      ? [
          {
            field: "revenue",
            headerName: "الربح (IQD)",
            flex: 1,
            renderCell: (params) =>
              parseInt(params.row?.revenue)?.toLocaleString(),
          },
        ]
      : []),
    {
      field: "is_hidden",
      headerName: "اخفاء المنتج",
      flex: 1,
      renderCell: (params) => {
        return (
          <Chip
            label={params.row.is_hidden ? "مخفي" : "مرئي"}
            color={params.row.is_hidden ? "default" : "success"}
          ></Chip>
        );
      },
    },
    {
      field: "is_active",
      headerName: "حالة المنتج",
      flex: 1,
      renderCell: (params) => {
        return (
          <Chip
            label={params.row.is_active ? "فعال" : "غير فعال"}
            color={params.row.is_active ? "success" : "error"}
            onClick={() => {
              handleActiveClick(params.row);
            }}
          ></Chip>
        );
      },
    },
    {
      field: "operations",
      headerName: "العمليات",
      flex: 1,
      renderCell: (params) => {
        return (
          <>
            <Permission permission="product_edit">
              <IconButton
                onClick={() => dispatch(Product.getById(params.row.id))}
              >
                <BiEdit />
              </IconButton>
            </Permission>
            <Permission permission="product_delete">
              <IconButton onClick={() => handleDeleteClick(params.row)}>
                <BiTrash />
              </IconButton>
            </Permission>
          </>
        );
      },
    },
  ];

  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch(Product.getAll(_SearchBody));
    }, 500);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_SearchBody]);

  useEffect(() => {
    dispatch(
      setSearchBody({
        search: search.search,
        sub_category_id: search.sub_category_id ?? "",
        skip: currentPage * page,
        take: page,
      })
    );
  }, [currentPage, page, search, dispatch]);

  return (
    <Card>
      <CardContent>
        <Box>
          <ConfirmationDialog
            open={openConfirm}
            title="تاكيد"
            message="هل انت متاكد من حذف"
            name={dialogData.name}
            confirmText="حذف"
            cancelText="الغاء"
            onConfirm={() => handleConfirmDelete()}
            onCancel={handleCancelDelete}
          />
          <ConfirmationDialog
            open={openActive}
            title="تاكيد"
            message={`هل انت متاكد من ${
              dialogActiveData.is_active ? "الغاء تفعيل المنتج" : "تفعيل المنتج"
            }`}
            name={dialogActiveData.user_name}
            confirmText="تاكيد"
            cancelText="الغاء"
            onConfirm={() => handleConfirmActive()}
            onCancel={handleCancelActive}
          />
        </Box>
        <Grid container rowSpacing={1} columnSpacing={2}>
          <Grid item xs={12} md={2}>
            <TextField
              size="small"
              onChange={(e) => {
                setSearch({
                  ...search,
                  search: e.target.value,
                });
              }}
              value={search.search}
              label="بحث عن الاسم "
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <SelectSubCategory
              isproduct
              onChange={(e, newValue) => {
                setSearch({
                  ...search,
                  sub_category_id: newValue ? newValue.id : null,
                });
              }}
              value={
                _SubCategory.filter((x) => x.id === search.sub_category_id)[0]
                  ? _SubCategory.filter(
                      (x) => x.id === search.sub_category_id
                    )[0]
                  : search.sub_category_id
              }
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Tooltip title="الغاء الفلتر">
              <IconButton
                onClick={() => {
                  setSearch({
                    search: "",
                    sub_category_id: null,
                  });
                  setPage(15);
                  setCurrentPage(0);
                }}
              >
                <MdOutlineClear />
              </IconButton>
            </Tooltip>
          </Grid>
          {smallScreen ? (
            _Product?.data?.length > 0 ? (
              <Grid item xs={12}>
                {loading ? <LinearProgress /> : null}
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {_Product.data.map((product, index) => (
                    <Card key={index} sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                        <Stack spacing={1.5}>
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="space-between"
                            alignItems="flex-start"
                            sx={{
                              pb: 1,
                              borderBottom: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <Typography
                              variant="h5"
                              color="primary"
                              sx={{ fontWeight: 600 }}
                            >
                              {product?.name}
                            </Typography>
                            <Typography variant="caption" noWrap>
                              #{product?.sequence}
                            </Typography>
                          </Stack>
                          <Typography variant="subtitle1">
                            التصنيف:{" "}
                            <strong>{product?.sub_category_name}</strong>
                          </Typography>
                          <Typography variant="subtitle1">
                            سعر البيع:{" "}
                            <strong>
                              {parseInt(product?.selling_price)?.toLocaleString()}
                            </strong>
                          </Typography>
                          {showRevenue ? (
                            <Typography variant="subtitle1">
                              الربح (IQD):{" "}
                              <strong>
                                {parseInt(product?.revenue)?.toLocaleString()}
                              </strong>
                            </Typography>
                          ) : null}
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              size="small"
                              label={product.is_hidden ? "مخفي" : "مرئي"}
                              color={product.is_hidden ? "default" : "success"}
                            />
                            <Chip
                              size="small"
                              label={product.is_active ? "فعال" : "غير فعال"}
                              color={product.is_active ? "success" : "error"}
                              onClick={() => handleActiveClick(product)}
                            />
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                            sx={{
                              pt: 1,
                              borderTop: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <Permission permission="product_edit">
                              <Tooltip title="تعديل">
                                <IconButton
                                  onClick={() =>
                                    dispatch(Product.getById(product.id))
                                  }
                                >
                                  <BiEdit />
                                </IconButton>
                              </Tooltip>
                            </Permission>
                            <Permission permission="product_delete">
                              <Tooltip title="حذف">
                                <IconButton
                                  onClick={() => handleDeleteClick(product)}
                                >
                                  <BiTrash />
                                </IconButton>
                              </Tooltip>
                            </Permission>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
                <Card sx={{ mt: 2, borderRadius: 2 }}>
                  <TablePagination
                    component="div"
                    count={_Product.total}
                    rowsPerPage={page}
                    page={currentPage}
                    onPageChange={(e, newPage) => setCurrentPage(newPage)}
                    rowsPerPageOptions={[15, 25, 50, 100]}
                    onRowsPerPageChange={(event) => {
                      setPage(parseInt(event.target.value, 10));
                    }}
                  />
                </Card>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Card sx={{ borderRadius: 2 }}>
                  {loading ? <LinearProgress /> : null}
                  <Typography m={2} variant="h5" color="primary" align="center">
                    لا توجد بيانات
                  </Typography>
                </Card>
              </Grid>
            )
          ) : (
            <Grid item xs={12}>
              <Box sx={{ height: "70vh", width: "100%" }}>
                <DataGrid
                  rows={_Product.data.filter((product, index) => {
                    return { ...product, index: index + 1 };
                  })}
                  columns={columns}
                  pageSize={page}
                  page={currentPage}
                  rowCount={_Product.total}
                  loading={loading}
                  rowHeight={50}
                  pagination
                  paginationMode="server"
                  selectionModel={[]}
                  onPageChange={(newPage) => setCurrentPage(newPage)}
                  rowsPerPageOptions={[15, 25, 50, 100]}
                  onPageSizeChange={(newPageSize) => {
                    setPage(newPageSize);
                  }}
                  components={{
                    LoadingOverlay: LinearProgress,
                  }}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ProductsTable;
